import React, { useState, useCallback } from 'react';
import MemoryChart from './MemoryChart';
import { useMoControllerStore } from '../stores/moControllerStore';
import { formatTime, formatDateTime } from '../utils/datetime';

interface PeerDetail {
  name: string;
  hash?: string;
  nat_type?: string;
  connection_quality: number;
}

interface DownloadSource {
  p2p: number;
  http: number;
}

interface DetailedStatus {
  clientHash?: string;
  timestamp?: string;
  authenticated?: boolean;
  peers?: {
    total: number;
    active: number;
    details?: PeerDetail[];
  };
  tickets?: {
    count: number;
    families?: string[];
  };
  downloads?: {
    active: boolean;
    queue: number;
    downloading: string[];
    downloading_names: Record<string, string>;
    downloading_progress: Record<string, number>;
    downloading_sources: Record<string, DownloadSource>;
    retry_count: number;
    cached_count: number;
    queue_names: Record<string, string>;
    cached_names: Record<string, string>;
  };
  player_connected?: boolean;
  player_status?: any;
}

interface MemoryStats {
  rss_kb: number;
  vm_size_kb: number;
  threads: number;
  fd_count: number;
}

interface ManagerStatus {
  node_id?: string;
  platform?: string;
  profile?: string;
  deploy_priority?: number;
  uptime_secs?: number;
  momanager_version?: string;
  components?: Record<string, {
    running: boolean;
    pid?: number | null;
    version: string;
  }>;
  memory?: Record<string, MemoryStats>;
  nix_store_free_bytes?: number | null;
  nix_store_total_bytes?: number | null;
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected';

interface MoClientMonitorProps {
  detailedStatus: DetailedStatus | null;
  managerStatus: ManagerStatus | null;
  connectionState: ConnectionState;
  clientHash: string;
}

const formatBytes = (bytes: number | null | undefined): string => {
  if (bytes == null || bytes < 0) return '-';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb.toFixed(1)}GB`;
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(0)}MB`;
};

// MoManager 의 NIX_STORE_MIN_FREE_BYTES (2GB) 기준 — 미달이면 system_switch 가
// preflight 에서 거부됨. UI 빨간 배지로 운영자에게 즉시 알림.
const NIX_STORE_PREFLIGHT_THRESHOLD_BYTES = 2 * 1024 * 1024 * 1024;
// 임박 (yellow) — preflight + 10% 마진 안에 들어오면 곧 disk-full 위험.
const NIX_STORE_WARNING_THRESHOLD_BYTES = NIX_STORE_PREFLIGHT_THRESHOLD_BYTES * 1.1;

const nixStoreSeverity = (free: number | null | undefined): 'critical' | 'warning' | 'ok' | 'unknown' => {
  if (free == null) return 'unknown';
  if (free < NIX_STORE_PREFLIGHT_THRESHOLD_BYTES) return 'critical';
  if (free < NIX_STORE_WARNING_THRESHOLD_BYTES) return 'warning';
  return 'ok';
};

const formatUptime = (secs: number | undefined): string => {
  if (secs == null) return '-';
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const shortHash = (hash: string): string => {
  if (hash.startsWith('MoClient-')) return hash.substring(0, 16);
  if (hash.startsWith('MoFamily-')) return hash.substring(0, 16);
  return hash.substring(0, 12);
};

const qualityColor = (q: number): string => {
  if (q >= 80) return 'text-green-400';
  if (q >= 50) return 'text-yellow-400';
  return 'text-red-400';
};

const qualityBar = (q: number): string => {
  const blocks = ['░', '▏', '▎', '▍', '▌', '▋', '▊', '▉', '█'];
  const filled = Math.floor(q / 10);
  const partial = Math.round((q % 10) / 10 * 8);
  let bar = '█'.repeat(filled);
  if (filled < 10) {
    bar += blocks[partial];
    bar += '░'.repeat(9 - filled);
  }
  return bar;
};

const progressBar = (pct: number, width: number = 20): string => {
  const filled = Math.round(pct / 100 * width);
  const empty = width - filled;
  return '█'.repeat(filled) + '░'.repeat(empty);
};

const progressColor = (pct: number): string => {
  if (pct >= 80) return 'text-green-400';
  if (pct >= 40) return 'text-yellow-400';
  return 'text-cyan-400';
};

const StatusDot: React.FC<{ active: boolean }> = ({ active }) => (
  <span className={active ? 'text-green-400' : 'text-red-400'}>
    {active ? '●' : '○'}
  </span>
);

const SectionHeader: React.FC<{ title: string; extra?: string }> = ({ title, extra }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="text-blue-400 font-bold">{title}</span>
    {extra && <span className="text-gray-500 text-xs">{extra}</span>}
    <span className="flex-1 border-t border-gray-700 self-center" />
  </div>
);

const MoClientMonitor: React.FC<MoClientMonitorProps> = ({
  detailedStatus,
  managerStatus,
  connectionState,
  clientHash,
}) => {
  const ds = detailedStatus;
  const ms = managerStatus;
  // 이 장비의 마지막 성공 업데이트(시각·버전) — 서버 update_logs 기준. node_id 로 조회.
  const [lastUpdate, setLastUpdate] = useState<{ event: string; version?: string | null; created_at: string } | null>(null);
  React.useEffect(() => {
    const nodeId = ms?.node_id;
    if (!nodeId) { setLastUpdate(null); return; }
    let cancelled = false;
    fetch(`/moupdate/node/${encodeURIComponent(nodeId)}/last-update`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (!cancelled) setLastUpdate(j?.data ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ms?.node_id]);
  // 원격 롤백(관리자) — SSH 차단 커스터머 장비의 복구 경로. 이전 NixOS 세대로 전환·재부팅.
  const [rollingBack, setRollingBack] = useState(false);
  const handleRollback = useCallback(async () => {
    const nodeId = ms?.node_id;
    if (!nodeId) return;
    if (!window.confirm('이 장비를 이전 NixOS 세대로 롤백하고 재부팅합니다. 계속할까요?')) return;
    setRollingBack(true);
    try {
      const r = await fetch('/moupdate/rollback', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_id: nodeId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) window.alert('롤백 실패: ' + (j.error || r.status));
      else window.alert('롤백 명령 전송됨. 장비가 이전 세대로 전환·재부팅합니다.');
    } catch {
      window.alert('롤백 요청 오류');
    } finally {
      setRollingBack(false);
    }
  }, [ms?.node_id]);
  // root 복구비번 조회(관리자) — SSH 차단 커스터머 장비의 콘솔 root 로그인용(장비별 LAPS).
  // 조회는 서버에서 감사 기록(retrieved_by/at)되며, 표시는 click-to-reveal + 토글.
  const [recoveryCred, setRecoveryCred] = useState<string | null>(null);
  const [loadingCred, setLoadingCred] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);
  const handleShowRecovery = useCallback(async () => {
    const nodeId = ms?.node_id;
    if (!nodeId) return;
    if (recoveryCred) { setRecoveryCred(null); setCredError(null); return; } // 토글: 숨김
    if (!window.confirm('이 장비의 root 복구비번을 조회합니다.\n조회 사실이 감사 기록(누가·언제)에 남습니다. 계속할까요?')) return;
    setLoadingCred(true);
    setCredError(null);
    try {
      const r = await fetch(`/moupdate/recovery-credential/${encodeURIComponent(nodeId)}`, { credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) setCredError(j.error || `조회 실패 (${r.status})`);
      else setRecoveryCred(j.data?.recovery_password ?? null);
    } catch {
      setCredError('조회 요청 오류');
    } finally {
      setLoadingCred(false);
    }
  }, [ms?.node_id, recoveryCred]);
  const peers = ds?.peers;
  const downloads = ds?.downloads;
  const tickets = ds?.tickets;
  const timestamp = ds?.timestamp ? formatTime(ds.timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '--:--:--';

  // 개별 파일 삭제. store 의 removeFile 액션은 selectedClientHash 를 자동으로 사용.
  const removeFile = useMoControllerStore(s => s.removeFile);
  const [removingHashes, setRemovingHashes] = useState<Set<string>>(new Set());
  const handleRemoveFile = useCallback(async (hash: string, name: string) => {
    const display = name && name.length > 0 ? name : hash;
    if (!window.confirm(`'${display}' 을(를) 디바이스에서 삭제할까요?\n재생 중이면 끝까지 재생 후 자동 삭제됩니다.\n슬롯에 여전히 있으면 잠시 후 다시 다운로드됩니다.`)) {
      return;
    }
    setRemovingHashes(prev => new Set(prev).add(hash));
    try {
      const result = await removeFile(hash);
      if (!result.success) {
        window.alert(`삭제 실패: ${result.error || 'unknown'}`);
      }
    } finally {
      setRemovingHashes(prev => {
        const next = new Set(prev);
        next.delete(hash);
        return next;
      });
    }
  }, [removeFile]);

  const connLabel = connectionState === 'connected'
    ? (ds ? 'Streaming' : 'Connected')
    : connectionState === 'connecting' ? 'Connecting...' : 'Disconnected';
  const connColor = connectionState === 'connected'
    ? (ds ? 'text-green-400' : 'text-yellow-400')
    : connectionState === 'connecting' ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="bg-gray-900 text-gray-200 font-mono text-xs p-3 rounded overflow-hidden select-text" style={{ minHeight: '320px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-700">
        <div className="flex items-center gap-3">
          <span className="text-white font-bold">{shortHash(clientHash)}</span>
          {ms?.node_id && (
            <span className="text-gray-500" title="MoManager 장비 정체성(machine-id 기반, 배포/복구비번). Screen(moclient)과 별개.">
              node: <span className="text-gray-400">{ms.node_id}</span>
            </span>
          )}
          <span className={connColor}>{connLabel}</span>
          {ds?.authenticated !== undefined && (
            <span className={ds.authenticated ? 'text-green-400' : 'text-red-400'}>
              {ds.authenticated ? 'AUTH' : 'NOAUTH'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-gray-400">
          <span>Last: {timestamp}</span>
          {ds?.player_connected !== undefined && (
            <span className="flex items-center gap-1">
              <StatusDot active={ds.player_connected} />
              <span className="text-gray-500">Player</span>
            </span>
          )}
        </div>
      </div>

      {/* Components (from manager_status) */}
      {ms?.components && (
        <div className="mb-2">
          <SectionHeader title="Components" extra={ms.platform || undefined} />
          <div className="flex flex-col gap-0.5 pl-1">
            {/* ⚠️ 컴포넌트 이름을 박아 두지 않는다. 종전엔 ['momanager','moclient','moplayer']
                만 그려서, 장비가 mpp_renderer 를 보내기 시작해도 화면에 영영 안 나왔다
                (2026-08-08 — 장비·서버·DB 는 전부 정상이었고 여기서만 잘렸다).
                데이터가 주는 것을 그대로 그리되, 순서만 고정해 읽기 편하게 한다. */}
            {(() => {
              const ORDER = ['momanager', 'moclient', 'moplayer', 'mpp_renderer'];
              const names = Object.keys(ms.components || {}).sort((a, b) => {
                const ia = ORDER.indexOf(a), ib = ORDER.indexOf(b);
                return (ia < 0 ? ORDER.length : ia) - (ib < 0 ? ORDER.length : ib) || a.localeCompare(b);
              });
              return names.map(name => {
              const c = ms.components?.[name];
              if (!c) return null;
              return (
                <div key={name} className="flex items-center gap-2">
                  <StatusDot active={c.running} />
                  <span className="text-gray-300 w-24">{name}</span>
                  <span className="text-gray-500">v{c.version}</span>
                  {c.pid && <span className="text-gray-600">pid:{c.pid}</span>}
                </div>
              );
              });
            })()}
          </div>
          {ms.uptime_secs != null && (
            <div className="pl-1 text-gray-500 mt-0.5">uptime: {formatUptime(ms.uptime_secs)}</div>
          )}
          {(ms.deploy_priority != null || ms.profile) && (
            <div className="pl-1 text-gray-500 mt-0.5 flex items-center gap-3">
              {ms.profile && <span>profile: <span className="text-gray-300">{ms.profile}</span></span>}
              {ms.deploy_priority != null && (
                <span className="flex items-center gap-1">
                  priority:
                  <span className={ms.deploy_priority < 100 ? 'text-amber-400 font-bold' : 'text-gray-300'}>{ms.deploy_priority}</span>
                  {ms.deploy_priority < 100 && (
                    <span className="px-1 py-0.5 text-[10px] bg-amber-800 text-amber-100 rounded">CANARY</span>
                  )}
                </span>
              )}
            </div>
          )}
          <div className="pl-1 text-gray-500 mt-0.5">
            마지막 업데이트: {lastUpdate ? formatDateTime(lastUpdate.created_at) : '이력 없음'}
            {lastUpdate?.version && <span className="text-gray-600"> (v{lastUpdate.version})</span>}
          </div>
          {ms?.node_id && (
            <div className="mt-1 ml-1 flex items-center gap-2 flex-wrap">
              <button
                onClick={handleRollback}
                disabled={rollingBack}
                title="이 장비를 이전 NixOS 세대로 롤백하고 재부팅합니다(원격 복구)."
                className="px-2 py-0.5 text-[11px] bg-red-900 text-red-200 rounded hover:bg-red-800 disabled:opacity-50"
              >
                {rollingBack ? '롤백 중…' : '이전 세대로 롤백'}
              </button>
              <button
                onClick={handleShowRecovery}
                disabled={loadingCred}
                title="이 장비의 root 복구비번을 조회합니다(콘솔 Ctrl+Alt+F2 → root 로그인용). 조회는 감사 기록됩니다."
                className="px-2 py-0.5 text-[11px] bg-amber-900 text-amber-200 rounded hover:bg-amber-800 disabled:opacity-50"
              >
                {loadingCred ? '조회 중…' : recoveryCred ? '복구비번 숨기기' : 'root 복구비번 보기'}
              </button>
              {recoveryCred && (
                <span className="flex items-center gap-2">
                  <span className="text-amber-300 font-bold select-all bg-gray-800 px-1.5 py-0.5 rounded tracking-wider">{recoveryCred}</span>
                  <span className="text-gray-600 text-[10px]">Ctrl+Alt+F2 → root · 조회 기록됨</span>
                </span>
              )}
              {credError && <span className="text-red-400">{credError}</span>}
            </div>
          )}
          {ms.nix_store_total_bytes != null && ms.nix_store_total_bytes > 0 && (() => {
            const free = ms.nix_store_free_bytes ?? 0;
            const total = ms.nix_store_total_bytes!;
            const usedPct = Math.max(0, Math.min(100, ((total - free) / total) * 100));
            const severity = nixStoreSeverity(ms.nix_store_free_bytes);
            const colorBar = severity === 'critical' ? 'text-red-400'
              : severity === 'warning' ? 'text-yellow-400'
              : 'text-green-400';
            const labelClass = severity === 'critical'
              ? 'text-red-400 font-bold'
              : severity === 'warning' ? 'text-yellow-400' : 'text-gray-400';
            const badge = severity === 'critical'
              ? <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-red-700 text-white rounded">PREFLIGHT FAIL</span>
              : severity === 'warning'
              ? <span className="ml-2 px-1.5 py-0.5 text-[10px] bg-yellow-700 text-white rounded">LOW</span>
              : null;
            return (
              <div className="pl-1 mt-0.5 flex items-center gap-2">
                <span className="text-gray-500">/nix/store:</span>
                <span className={`${colorBar} font-mono`}>{progressBar(usedPct, 16)}</span>
                <span className={labelClass}>
                  {formatBytes(free)} free / {formatBytes(total)}
                </span>
                {badge}
              </div>
            );
          })()}
        </div>
      )}

      {!ds && (
        <div className="text-gray-500 text-center py-8">
          {connectionState === 'connected' ? 'Waiting for detailed status...' : 'Not connected'}
        </div>
      )}

      {ds && (
        <div className="grid grid-cols-2 gap-3">
          {/* Left: Peers */}
          <div>
            <SectionHeader
              title="Peers"
              extra={peers ? `${peers.active}/${peers.total} active` : undefined}
            />
            <div className="pl-1 space-y-0.5 max-h-40 overflow-y-auto">
              {(!peers?.details || peers.details.length === 0) ? (
                <div className="text-gray-600">(no peers)</div>
              ) : (
                peers.details.map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className={`${qualityColor(p.connection_quality)} w-24`}>
                      {qualityBar(p.connection_quality)}
                    </span>
                    <span className="text-gray-300">{p.name || shortHash(p.hash || '')}</span>
                    <span className={qualityColor(p.connection_quality)}>
                      {p.connection_quality}
                    </span>
                    {p.nat_type && (
                      <span className="text-gray-600 text-[10px]">{p.nat_type}</span>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Tickets */}
            <div className="mt-2">
              <SectionHeader title="Tickets" extra={tickets ? `${tickets.count}` : undefined} />
              <div className="pl-1 text-gray-400 max-h-16 overflow-y-auto">
                {(!tickets?.families || tickets.families.length === 0) ? (
                  <span className="text-gray-600">(none)</span>
                ) : (
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {tickets.families.map((f, i) => (
                      <span key={i} className="text-gray-400">{shortHash(f)}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Downloads */}
          <div>
            <SectionHeader title="Downloads" />
            {!downloads ? (
              <div className="pl-1 text-gray-600">(no data)</div>
            ) : (
              <div className="pl-1">
                {/* Summary */}
                <div className="flex gap-3 mb-1.5 text-gray-400">
                  <span>{(Array.isArray(downloads.downloading) ? downloads.downloading.length : 0) + (typeof downloads.queue === 'number' ? downloads.queue : 0) + (downloads.cached_count || 0)} files</span>
                  <span className="text-cyan-400">{Array.isArray(downloads.downloading) ? downloads.downloading.length : 0} active</span>
                  <span className="text-yellow-400">{typeof downloads.queue === 'number' ? downloads.queue : 0} queued</span>
                  <span className="text-green-400">{downloads.cached_count || 0} cached</span>
                  {(downloads.retry_count || 0) > 0 && (
                    <span className="text-red-400">{downloads.retry_count} retries</span>
                  )}
                </div>

                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {/* Downloading */}
                  {Array.isArray(downloads.downloading) && downloads.downloading.map((hash) => {
                    const name = downloads.downloading_names?.[hash] || hash;
                    // 백엔드는 Vec<bool> (청크별 완료 여부) 반환. % 계산.
                    const progressRaw = downloads.downloading_progress?.[hash];
                    let pct = 0;
                    if (Array.isArray(progressRaw) && progressRaw.length > 0) {
                      const done = progressRaw.filter(Boolean).length;
                      pct = (done / progressRaw.length) * 100;
                    } else if (typeof progressRaw === 'number') {
                      pct = progressRaw;
                    }
                    const src = downloads.downloading_sources?.[hash];
                    const displayName = name.length > 28 ? name.slice(0, 25) + '...' : name;
                    return (
                      <div key={hash}>
                        <div className="flex items-center justify-between">
                          <span className="text-white truncate">{displayName}</span>
                          <span className={progressColor(pct)}>{pct.toFixed(0)}%</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={progressColor(pct)}>
                            {progressBar(pct)}
                          </span>
                          {src && (
                            <span className="text-gray-500">
                              P2P:{src.p2p} HTTP:{src.http}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Queued */}
                  {downloads.queue_names && Object.entries(downloads.queue_names).map(([hash, name]) => (
                    <div key={`q-${hash}`} className="flex items-center justify-between">
                      <span className="text-gray-400 truncate">
                        {(name || hash).length > 28 ? (name || hash).slice(0, 25) + '...' : (name || hash)}
                      </span>
                      <span className="text-yellow-400">waiting</span>
                    </div>
                  ))}

                  {/* Cached — 각 row 에 ✕ 버튼으로 개별 삭제 */}
                  {downloads.cached_names && Object.entries(downloads.cached_names).map(([hash, name]) => {
                    const removing = removingHashes.has(hash);
                    return (
                      <div key={`c-${hash}`} className="flex items-center justify-between gap-2">
                        <span className="text-gray-500 truncate flex-1 min-w-0">
                          {(name || hash).length > 28 ? (name || hash).slice(0, 25) + '...' : (name || hash)}
                        </span>
                        <span className="text-green-400 shrink-0">cached</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(hash, name)}
                          disabled={removing}
                          title="이 파일을 디바이스에서 삭제"
                          className="text-red-500 hover:text-red-300 disabled:text-gray-600 px-1 leading-none shrink-0"
                        >
                          {removing ? '…' : '×'}
                        </button>
                      </div>
                    );
                  })}

                  {(!Array.isArray(downloads.downloading) || downloads.downloading.length === 0) &&
                   (!downloads.queue_names || Object.keys(downloads.queue_names).length === 0) &&
                   (!downloads.cached_names || Object.keys(downloads.cached_names).length === 0) && (
                    <div className="text-gray-600">(no files)</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Memory Profiling */}
      {ms?.node_id && (
        <MemoryChartSection nodeId={ms.node_id} memory={ms.memory} />
      )}
    </div>
  );
};

/** Wrapper that manages profiling toggle state */
const MemoryChartSection: React.FC<{ nodeId: string; memory?: Record<string, MemoryStats> }> = ({ nodeId, memory }) => {
  const [enabled, setEnabled] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Load initial state from API
  React.useEffect(() => {
    fetch(`/moclient-test/memory-profiling/${nodeId}`)
      .then(r => r.json())
      .then(j => { if (j.success) setEnabled(j.enabled); setInitialized(true); })
      .catch(() => setInitialized(true));
  }, [nodeId]);

  const handleToggle = useCallback(async () => {
    const newState = !enabled;
    try {
      const res = await fetch(`/moclient-test/memory-profiling/${nodeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newState }),
      });
      const json = await res.json();
      if (json.success) setEnabled(newState);
    } catch (e) {
      console.error('Failed to toggle profiling:', e);
    }
  }, [nodeId, enabled]);

  if (!initialized) return null;

  return (
    <div className="mt-2">
      <SectionHeader title="Memory" extra={
        memory ? Object.entries(memory).map(([k, v]) =>
          `${k}: ${(v.rss_kb / 1024).toFixed(0)}M`
        ).join(' | ') : undefined
      } />
      <MemoryChart nodeId={nodeId} enabled={enabled} onToggle={handleToggle} />
    </div>
  );
};

export default MoClientMonitor;
